// @ts-strict-ignore
import { IArticleLocationProcessor, InsertedArticleData } from './i-article-location.processor';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleLocationVO } from '@article/model/vo/article-location.vo';
import { ArticleManager } from '@article/manager/article.manager';
import { SelectedConnectionsReferenceArticleLocationVO } from '@article/model/vo/selected-connections-reference-article-location.vo';
import { SelectedArticlesReferenceArticleLocationVO } from '@article/model/vo/selected-articles-reference-article-location.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleCreationManager } from '../manager/article-creation.manager';
import { PropertyCreator } from '@article/util/property-creator';
import { ConnectionCubeSearchRequirements } from '@article/components/connections/connection-creation/search/connection-cube-search-requirements';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { Log } from '@shared/debug/log';
import { ArticleCreationManagerFactory } from '../manager/article-creation.manager.factory';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { RemoveProjectArticleService } from '@project/service/remove-project-article.service';
import { AccessoryService, ConnectionSearchResult } from '@article/service/accessory.service';
import { AddArticleError } from './add-article-error';
import { lastValueFrom } from 'rxjs';

export class AddArticleToSelectedConnectionsLocationProcessor implements IArticleLocationProcessor {

  private _articleCreationManager: ArticleCreationManager;

  private _articleHead: ArticleVO;
  private _insertedArticleGUIDs: string[] = [];

  constructor(
    private _articleLocation: ArticleLocationVO,
    private _article: ArticleVO,
    private removeProjectArticleService: RemoveProjectArticleService,
    private articleManager: ArticleManager,
    private localeResourceManager: LocaleResourceManager,
    private propertyCreator: PropertyCreator,
    articleCreationManagerFactory: ArticleCreationManagerFactory,
    private accessoryService: AccessoryService
  ) {
    this._articleHead = this._article;
    this._articleCreationManager = articleCreationManagerFactory.getNewInstance();
  }

  public async process(): Promise<InsertedArticleData> {
    try {
      await this.loadFullArticle();
    } catch (error) {
      const errorMessageTranslationKey = await this.processArticleFailure(`Could not load article: `, error);
      throw new AddArticleError(errorMessageTranslationKey);
    }

    let connectionCubeSearchRequirements: ConnectionCubeSearchRequirements[];

    try {
      connectionCubeSearchRequirements = this.processingFullArticle();
    } catch (error) {
      await this.processFailure(`Could not process article: `, error);
      throw new AddArticleError(error.message);
    }

    let connectionSearchResult: ConnectionSearchResult;

    try {
      connectionSearchResult = await this.loadConnectionsByRequirements(connectionCubeSearchRequirements);
    } catch (error) {
      await this.processFailure(`Could not load connections: `, error);
      throw new AddArticleError(error.message);
    }

    return this.insertArticle(connectionSearchResult);
  }

  private async loadFullArticle(): Promise<void> {
    const loadedArticleFromCache =  this.articleManager.getCachedArticle(this._article.identity, this._article.version);

    if (loadedArticleFromCache) {
      this._article = loadedArticleFromCache;
      return;
    }

    this._article = await lastValueFrom(this.articleManager.newLoadArticle_handler(this._article.identity, this._article.version));
  }

  private getFilteredAcceptingCubes(): Connection[] {
    if (this._articleLocation instanceof SelectedConnectionsReferenceArticleLocationVO) {
      return ConnectionInfoUtils.filterAcceptingCubes(this._articleLocation.acceptingCubes);
    } else if (this._articleLocation instanceof SelectedArticlesReferenceArticleLocationVO) {
      const connectionCubes: Connection[] = ConnectionInfoUtils.findConnectionsInArticles(this._articleLocation.articleContainers);
      return ConnectionInfoUtils.filterAcceptingCubes(connectionCubes);
    } else {
      return null;
    }
  }

  private processingFullArticle(): ConnectionCubeSearchRequirements[] {
    const filteredAcceptingCubes: Connection[] = this.getFilteredAcceptingCubes();

    if (!filteredAcceptingCubes || filteredAcceptingCubes.length  === 0) {
      const message: string = this.localeResourceManager.getValueByKey('addArticleToSelectedConnectionLocationProcessor.message_error_connecting_article', [this._articleHead.articleNo]);
      throw new AddArticleError(message);
    }

    return this.propertyCreator.createCubesSearchRequirementsByDefault(filteredAcceptingCubes);
  }

  private async loadConnectionsByRequirements(connectionCubeSearchRequirements: ConnectionCubeSearchRequirements[]): Promise<ConnectionSearchResult> {
    const connectionSearchResult =
      await lastValueFrom(this.accessoryService.getArticlesByRequirementsAndArticle(connectionCubeSearchRequirements, this._article));

    if (!connectionSearchResult.vo?.connections || connectionSearchResult.vo.connections.length === 0) {
      const message: string = this.localeResourceManager.getValueByKey('addArticleToSelectedConnectionLocationProcessor.message_error_connecting_article', [this._articleHead.articleNo]);
      throw new AddArticleError(message);
    }

    return connectionSearchResult;
  }

  private async insertArticle(connectionSearchResult: ConnectionSearchResult): Promise<InsertedArticleData> {
    this._articleCreationManager.createUndoOperation = false;
    await this._articleCreationManager.createArticlesBySuitableConnections(connectionSearchResult.vo.connections[0], connectionSearchResult.connectionCubeVectors);
    this._insertedArticleGUIDs = this._insertedArticleGUIDs.concat(ArticleContainerUtils.extractArticleGUIDs(this._articleCreationManager.getCreatedArticleContainers()));

    return {
      baseArticleGUID: null,
      insertedArticleGUIDs: this._insertedArticleGUIDs,
      moveArticlesMap: null,
      articleLocation: this._articleLocation,
      createUndoOperation: true,
      undoOperations: this._articleCreationManager.createdUndoOperations,
      isAccessoriesAdded: true
    };
  }

  private async processArticleFailure(...messageToLog: any): Promise<string> {
    const message: string = this.localeResourceManager.getValueByKey('addArticleToSelectedConnectionLocationProcessor.message.error_load_article', [
      this._articleHead.articleNo,
    ]);
    await this.processFailure(messageToLog);
    return message;
  }

  private async processFailure(...messageToLog: any): Promise<void> {
    Log.error(messageToLog)
    await this.removeProjectArticleService.removeArticles(this._insertedArticleGUIDs);
  }
}
